import React, { useState, useEffect } from 'react';
import axios from 'axios';
import LoadingSpinner from './LoadingSpinner';
import ProjectModal from './ProjectModal';
import AnalysisRunModal from './AnalysisRunModal';

const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

const ProjectsTable = ({ onBack, onLogout }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [analysisProject, setAnalysisProject] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) { if (onLogout) onLogout(); return; }
      const response = await axios.get(`${API_URL}/projects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProjects(response.data);
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 401) { if (onLogout) onLogout(); return; }
      else if (err.response?.status === 403) setError('Access denied. Admin privileges required.');
      else setError('Failed to load projects.');
      setLoading(false);
    }
  };

  const handleDeleteProject = async (projectName) => {
    const project = projects.find(p => p.name === projectName);
    const displayLabel = project?.displayName || projectName;
    if (!window.confirm(`Are you sure you want to delete project "${displayLabel}"?`)) return;
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${API_URL}/projects/${encodeURIComponent(projectName)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccessMessage('Project deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchProjects();
    } catch (err) {
      setError('Failed to delete project: ' + (err.response?.data?.message || err.message));
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleModalSave = async (projectData, originalName) => {
    try {
      const token = localStorage.getItem('authToken');
      if (editingProject) {
        await axios.put(`${API_URL}/projects/${encodeURIComponent(originalName)}`, projectData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccessMessage('Project updated successfully');
      } else {
        await axios.post(`${API_URL}/projects`, projectData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccessMessage('Project created successfully');
      }
      setTimeout(() => setSuccessMessage(null), 3000);
      setIsModalOpen(false);
      setEditingProject(null);
      fetchProjects();
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-96"><LoadingSpinner /></div>;

  if (error && !projects.length) return (
    <div className="p-4">
      <div className="p-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400">{error}</div>
      <button onClick={onBack} className="mt-4 text-white bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800">
        Back to Admin Panel
      </button>
    </div>
  );

  return (
    <div className="p-4">
      {/* Header */}
      <div className="p-4 mb-4 bg-white border border-gray-200 rounded-lg shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <button onClick={onBack} className="inline-flex items-center text-sm font-medium text-primary-700 hover:text-primary-800 dark:text-primary-500 dark:hover:text-primary-400 mb-2">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
              Back to Admin
            </button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Project Management</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Add, edit, or remove project configurations</p>
          </div>
          <button onClick={() => { setEditingProject(null); setIsModalOpen(true); }}
            className="inline-flex items-center text-white bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800">
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" /></svg>
            Add Project
          </button>
        </div>
      </div>

      {successMessage && (
        <div className="p-4 mb-4 text-sm text-green-800 rounded-lg bg-green-50 dark:bg-gray-800 dark:text-green-400">{successMessage}</div>
      )}
      {error && (
        <div className="p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400">{error}</div>
      )}

      {/* Table */}
      <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3">Portfolio</th>
              <th className="px-4 py-3">Account</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {projects.length === 0 ? (
              <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">No projects found. Click "Add Project" to create one.</td></tr>
            ) : projects.map((project) => (
              <tr key={project.name} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                  {project.displayName || project.name}
                  {project.displayName && <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">({project.name})</span>}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    project.status === 'active'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                  }`}>{project.status === 'active' ? 'Active' : 'Inactive'}</span>
                </td>
                <td className="px-4 py-3">{project.sourceType}</td>
                <td className="px-4 py-3">{project.portfolio}</td>
                <td className="px-4 py-3">{project.account}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-2">
                    <button onClick={() => setAnalysisProject({ name: project.name, displayName: project.displayName })}
                      className="p-1.5 rounded-lg text-green-600 hover:text-green-800 hover:bg-green-100 dark:text-green-500 dark:hover:text-green-400 dark:hover:bg-green-900/30 transition-colors"
                      title="Run Analysis">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button onClick={() => { setEditingProject(project); setIsModalOpen(true); }}
                      className="font-medium text-primary-600 hover:text-primary-800 dark:text-primary-500 dark:hover:text-primary-400">Edit</button>
                    <button onClick={() => handleDeleteProject(project.name)}
                      className="font-medium text-red-600 hover:text-red-800 dark:text-red-500 dark:hover:text-red-400">Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <ProjectModal project={editingProject} projects={projects} onClose={() => { setIsModalOpen(false); setEditingProject(null); }} onSave={handleModalSave} />
      )}

      <AnalysisRunModal
        isOpen={!!analysisProject}
        onClose={() => setAnalysisProject(null)}
        projectName={analysisProject?.name}
        displayName={analysisProject?.displayName}
      />
    </div>
  );
};

export default ProjectsTable;
